import React, { useState, useEffect } from "react";
import { Routes, Route, useLocation } from "react-router-dom";
import OnboardingModal from "./components/TeleconsultOnDemand/OnboardingModal";
import PopUpModal from "./components/ProviderApp/PopUpModal";
import IncomingRequestModal from "./components/TeleconsultOnDemand/IncomingRequestModal";
import NewsletterConsentModal from "./components/Profile/NewsletterConsentModal";
import CallAlertsPromptModal from "./components/TeleconsultOnDemand/CallAlertsPromptModal";

// SCSS Files
import "./index.scss";
import "components/DesktopNavbar/DesktopNavbar.scss";
import "components/DesktopSidebar/DesktopSidebar.scss";
import "components/MobileNavbar/MobileNavbar.scss";
import "components/ConfirmAttendance/ConfirmationCodeInput.scss";
import "components/ClaimDetails/ClaimDetails.scss";
import "pages/Claims/Claims.scss";
import "pages/Claims/ClaimPrint.scss";
import "pages/EmployeeBenefits/EmployeeBenefits.scss";
import "pages/GuaranteeLetter/GuaranteeLetter.scss";
import "pages/Login/Login.scss";
import "pages/Terms/Terms.scss";
import "pages/Appointments/Appointments.scss";
import "pages/Referral/Referral.scss";
import "pages/Notification/Notification.scss";
import "pages/AuditLogs/AuditLogs.scss";
import "pages/SmsReminder/SmsReminder.scss";

// Navbar, Sidebar & Auth
import AuthService from "./services/auth.service";
import WithNavigation from "./layouts/WithNavigation";
import { ComponentError } from "./layouts/ComponentError";

// Pages
import { EmployeeBenefits } from "./pages/EmployeeBenefits/EmployeeBenefits";
import Login from "./pages/Login/Login";
import AssistantLogin from "./pages/AssistantLogin/AssistantLogin";
import AssistantDashboard from "./pages/AssistantDashboard/AssistantDashboard";
import AssistantClaims from "./pages/AssistantClaims/AssistantClaims";
import SelectLocation from "./pages/SelectLocation/SelectLocation";
import Preregistration from "./pages/Preregistration/Preregistration";
import Claims from "./pages/Claims/Claims";
import ClaimPrint from "./pages/Claims/ClaimPrint";
import GuaranteeLetter from "./pages/GuaranteeLetter/GuaranteeLetter";
import Profile from "./pages/Profile/Profile";
import { Contact } from "./pages/Contact/Contact";
import Terms from "./pages/Terms/Terms";
import PrivacyPolicy from "./pages/PrivacyPolicy/PrivacyPolicy";
// import Appointments             from './pages/Appointments/Appointments';
// import Availability             from './pages/Availability/Availability';
import Teleconsult from "./pages/Teleconsult/Teleconsult";
import Referral from "./pages/Referral/Referral";
import Notification from "./pages/Notification/Notification";
import AuditLogs from "./pages/AuditLogs/AuditLogs";
import SmsReminder from "./pages/SmsReminder/SmsReminder";
import SmsReminderLogs from "./pages/SmsReminder/SmsReminderLogs";
import Meals from "./pages/Meal/Meal";
import DownloadProvider from "./pages/DownloadProvider/DownloadProvider";
import Prescription from "./pages/Prescription/Prescription";
import OnDemandVideo from "./pages/ZoomVideo/OnDemandVideo";
import Wallet from "./pages/Wallet/index";
import WalletWithdraw from "./pages/Wallet/Withdraw";

// EB QR
import { QRCodeTemplate } from "./components/QrCodeTemplate/QrCodeTemplate";

// import { Dashboard }         from './pages/Dashboard/Dashboard';
// import { About }             from './pages/About/About';
// import { Services }          from './pages/Services/Services';

import { ToastContainer } from "react-toastify";

const App = (props) => {
  // console.log({ REACT_APP_ENVIRONMENT: process.env.REACT_APP_ENVIRONMENT, REACT_APP_API_ENDPOINT: process.env.REACT_APP_API_ENDPOINT });
  const [profileDropdown, setProfileDropdown] = useState(false);
  const locationSet = !!AuthService.getPracticeLocation();

  useEffect(() => {
    const handleStorageChange = (event) => {
      if (event.key === 'teleconsult:on-demand-session-ended') {
        window.location.reload();
      }
    };
    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  // Search parameters in JSON format.
  const useQuery = () => new URLSearchParams(useLocation().search);
  let params = {};

  for (var [key, value] of useQuery().entries()) {
    params[key] = value;
  }

  const toggleProfileDropdown = (element) => {
    var newState;
    var popup = document.getElementById("profile-popup");

    if (element.target.id === "account-btn") {
      newState = !profileDropdown;
    } else {
      newState = popup ? popup.contains(element.target) : false;
    }

    setProfileDropdown(newState);
  };

  const currentUser = AuthService.getCurrentUser();
  const currentPath = useLocation().pathname;
  const authNeeded = !currentPath.match(/\/eb\/(login|assistant\/login)/);
  const onDemandOnboarded = localStorage.getItem(
    "teleconsult:ondemand-onboarded"
  );
  const downloadProviderSeen = localStorage.getItem("download-provider:seen");
  const newsletterConsentPromptSeen = localStorage.getItem(`newsletter-consent-prompt:seen:${currentUser?.id}`);
  const callAlertsPromptSeen = localStorage.getItem(`teleconsult:call-alerts-seen:${currentUser?.id}`);

  if (authNeeded && !currentUser) {
    window.location.replace("/eb/login");
  } else if (currentUser) {
    if (!authNeeded) {
      window.location.replace("/eb");
    } else if (currentUser.user_type === "assistant") {
      // For assistants, only allow to dashboard, terms, privacy and contact us pages.

      const preselectionPages = [
        "/eb/assistant/dashboard",
        "/eb/assistant/claims",
        "/eb/profile",
        "/eb/contact",
        "/eb/terms",
        "/eb/privacy",
        "/eb/print-claim/",
      ];

      const matchedPath = preselectionPages.filter((path) => {
        return currentPath.includes(path);
      });

      if (matchedPath.length === 0) {
        window.location.replace("/eb/assistant/dashboard");
      }
    } else if (currentUser.user_type === "doctor") {
      if (!locationSet && currentPath !== "/eb/select_location") {
        window.location.replace("/eb/select_location");
      } else if (!!locationSet && currentPath === "/eb/select_location") {
        window.location.replace("/eb/");
      }
    }
  }

  return (
    <div id="main-body" onClick={(element) => toggleProfileDropdown(element)}>
      <div>
        <Routes>
          <Route path="eb">
            <Route path="login" element={<Login />} />
            <Route path="assistant/login" element={<AssistantLogin />} />
            <Route path="print-claim/:id" element={<ClaimPrint />} />
            <Route path="print-qr-code" element={<QRCodeTemplate />} />
            <Route path="video" element={<OnDemandVideo />}/>
          </Route>

          <Route
            path="eb"
            element={<WithNavigation dropdownDisplayed={profileDropdown} />}
          >
            <Route exact path="" element={<EmployeeBenefits />} />
            <Route path="claims" element={<Claims params={params} />} />
            <Route path="contact" element={<Contact />} />
            <Route path="profile" element={<Profile />} />
            <Route path="terms" element={<Terms />} />
            <Route path="privacy" element={<PrivacyPolicy />} />
            <Route path="select_location" element={<SelectLocation />} />
            <Route
              path="preregistration"
              element={<Preregistration params={params} />}
            />
            <Route
              path="guarantee_letter"
              element={<GuaranteeLetter params={params} />}
            />
            {/* <Route path="appointments"            element={ <Appointments/> } />
            <Route path="availability"            element={ <Availability/> } /> */}
            <Route path="teleconsult" element={<Teleconsult />} />
            <Route path="referral_logs" element={<Referral />} />
            <Route path="audit_logs" element={<AuditLogs />} />
            <Route path="sms_reminder" element={<SmsReminder />} />
            <Route path="sms_reminder_logs" element={<SmsReminderLogs />} />
            <Route path="meals" element={<Meals />} />
            {/*<Route path="about"                   element={ <About/> } />*/}
            {/*<Route path="services"                element={ <Services/> } />*/}
            <Route path="notifications"           element={ <Notification /> } />
            <Route path="prescriptions" element={ <Prescription/> } />
            <Route path="download-provider" element={<DownloadProvider />} />
            <Route path="wallet" element={<Wallet />} />
            <Route path="wallet/withdraw" element={<WalletWithdraw />} />

            <Route path="assistant">
              <Route path="dashboard" element={<AssistantDashboard />} />
              <Route path="claims" element={<AssistantClaims />} />
            </Route>
          </Route>

          <Route path="*" element={<ComponentError />} />
        </Routes>
        <ToastContainer style={{ width: "400px" }} />
        {currentUser?.teleconsult_on_demand?.enabled === true &&
          onDemandOnboarded === null &&
          locationSet &&
          currentPath !== "/eb/select_location" && <OnboardingModal />}
        {downloadProviderSeen === null &&
          locationSet &&
          currentPath !== "/eb/download-provider" && <PopUpModal />}
        {newsletterConsentPromptSeen === null &&
          locationSet && 
          currentUser?.user_type === 'doctor' &&
          currentUser?.marketing_email_enabled !== true &&
          currentPath !== '/eb/select_location' &&
          currentPath !== '/eb/privacy' && <NewsletterConsentModal/>}
        {currentUser?.teleconsult_on_demand?.enabled === true &&
          onDemandOnboarded !== null &&
          callAlertsPromptSeen === null &&
          locationSet &&
          currentPath !== "/eb/select_location" && <CallAlertsPromptModal />}
        <IncomingRequestModal />
      </div>
    </div>
  );
};

export default App;
